import { isNil } from 'lodash';
import {
  IBasicEventAddCategoryMutation,
  IBasicEventRemoveCategoryMutation,
  IEntityCategoryFieldsFragment,
  IError,
  IFieldAddCategoryMutation,
  IFieldRemoveCategoryMutation,
  INamedBucketDefAddCategoryMutation,
  INamedBucketDefRemoveCategoryMutation,
  INamedCohortAddCategoryMutation,
  INamedCohortRemoveCategoryMutation,
  INamedFilterAddCategoryMutation,
  INamedFilterRemoveCategoryMutation,
  INamedMeasureAddCategoryMutation,
  INamedMeasureRemoveCategoryMutation,
  IVirtualEventAddCategoryMutation,
  IVirtualEventRemoveCategoryMutation,
} from '../../utils/apollo/graphqlSchema';
import printGraphqlErrors from '../../utils/apollo/printGraphqlErrors';

export const generateTagActions = ({
  existingTags = [],
  newTags,
}: {
  existingTags: IEntityCategoryFieldsFragment[];
  newTags: string[];
}): {
  tagsToAdd: string[];
  tagsToRemove: string[];
} => {
  const tagsToAdd = newTags.filter(
    (newTag) => !existingTags.some((existingTag) => existingTag.id === newTag),
  );
  const tagsToRemove = existingTags
    .filter((existingTag) => !newTags.includes(existingTag.id))
    .map((t) => t.id);

  return {
    tagsToAdd,
    tagsToRemove,
  };
};
export enum CategoryEntityType {
  Cohort = 'COHORT',
  Measure = 'MEASURE',
  VirtualEvent = 'VIRTUAL_EVENT',
  NamedFilter = 'NAMED_FILTER',
  NamedBucketDef = 'NAMED_BUCKET_DEF',
  Field = 'FIELD',
  BasicEvent = 'BASIC_EVENT',
}

export enum CategoryActionType {
  Add = 'ADD',
  Remove = 'REMOVE',
}

export const formatEntityCategoryErrors = ({
  entityType,
  data,
  actionType,
}: {
  entityType: CategoryEntityType;
  data?:
    | INamedCohortAddCategoryMutation
    | INamedMeasureAddCategoryMutation
    | INamedCohortRemoveCategoryMutation
    | INamedMeasureRemoveCategoryMutation
    | IVirtualEventAddCategoryMutation
    | IVirtualEventRemoveCategoryMutation
    | INamedFilterAddCategoryMutation
    | INamedFilterRemoveCategoryMutation
    | INamedBucketDefAddCategoryMutation
    | INamedBucketDefRemoveCategoryMutation
    | IFieldAddCategoryMutation
    | IFieldRemoveCategoryMutation
    | IBasicEventAddCategoryMutation
    | IBasicEventRemoveCategoryMutation
    | null;
  actionType: CategoryActionType;
}) => {
  if (isNil(data)) {
    return false;
  }

  // Use a nested mapping of dictionaryType and actionType to access the correct errors property
  const errorMappings: {
    [key in CategoryEntityType]: {
      [key in CategoryActionType]: (data: any) => IError[] | undefined;
    };
  } = {
    [CategoryEntityType.Cohort]: {
      [CategoryActionType.Add]: (data: INamedCohortAddCategoryMutation) =>
        data.namedCohortAddCategory.errors,
      [CategoryActionType.Remove]: (data: INamedCohortRemoveCategoryMutation) =>
        data.namedCohortRemoveCategory.errors,
    },
    [CategoryEntityType.Measure]: {
      [CategoryActionType.Add]: (data: INamedMeasureAddCategoryMutation) =>
        data.namedMeasureAddCategory.errors,
      [CategoryActionType.Remove]: (
        data: INamedMeasureRemoveCategoryMutation,
      ) => data.namedMeasureRemoveCategory.errors,
    },
    [CategoryEntityType.VirtualEvent]: {
      [CategoryActionType.Add]: (data: IVirtualEventAddCategoryMutation) =>
        data.virtualEventAddCategory.errors,
      [CategoryActionType.Remove]: (
        data: IVirtualEventRemoveCategoryMutation,
      ) => data.virtualEventRemoveCategory.errors,
    },
    [CategoryEntityType.NamedFilter]: {
      [CategoryActionType.Add]: (data: INamedFilterAddCategoryMutation) =>
        data.namedFilterAddCategory.errors,
      [CategoryActionType.Remove]: (data: INamedFilterRemoveCategoryMutation) =>
        data.namedFilterRemoveCategory.errors,
    },
    [CategoryEntityType.NamedBucketDef]: {
      [CategoryActionType.Add]: (data: INamedBucketDefAddCategoryMutation) =>
        data.namedBucketDefAddCategory.errors,
      [CategoryActionType.Remove]: (
        data: INamedBucketDefRemoveCategoryMutation,
      ) => data.namedBucketDefRemoveCategory.errors,
    },
    [CategoryEntityType.Field]: {
      [CategoryActionType.Add]: (data: IFieldAddCategoryMutation) =>
        data.fieldAddCategory.errors,
      [CategoryActionType.Remove]: (data: IFieldRemoveCategoryMutation) =>
        data.fieldRemoveCategory.errors,
    },
    [CategoryEntityType.BasicEvent]: {
      [CategoryActionType.Add]: (data: IBasicEventAddCategoryMutation) =>
        data.basicEventAddCategory.errors,
      [CategoryActionType.Remove]: (data: IBasicEventRemoveCategoryMutation) =>
        data.basicEventRemoveCategory.errors,
    },
  };

  const getErrors = errorMappings[entityType]?.[actionType];
  const errors = getErrors ? getErrors(data) : null;

  if (errors && printGraphqlErrors(errors)) {
    return true;
  } else {
    return false;
  }
};

export const defaultManagedOption = {
  label: 'Managed',
  value: 'managed',
};
